import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, ContentChild, TemplateRef, ViewChildren, QueryList, ElementRef, ViewChild } from '@angular/core';
import { DataElement, ReferencePhrase } from 'src/app/shared/models/common';
import {
  RevStatus,
  RevRule,
  RevDocStatusEnum,
} from 'src/app/shared/models/rev.model';
import { TranslateService } from '@ngx-translate/core';
import { NgRedux } from '@redux/redux-compatibility.service';
import { AppState, actionsList } from 'src/app/shared/redux/store';
import { RevService } from 'src/app/shared/services/rev.service';
import { SharedService } from 'src/app/shared/services/shared.service';
import { Unsubscribe } from 'redux';
import { ClaimToolElement } from '@shared/models/claim-tool-element.model';
import { take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ReevalJobService } from '@shared/services/reeval-job.service';
import { ReevalJobTarget } from '@shared/models/reeval-job.model';
import { CommonService } from '@shared/services/common.service';
import { ParentTileChildrenSelectionResponse } from '@shared/models/parent-tile-children-selection-response.model';
import { Config } from 'src/app/config';
import { PropagateActionsFromParentService } from '@shared/services/propagate-actions-from-parent.service';
import { ReferenceDocument } from '@shared/models/cat.model';
import { DocumentPositionSyncService } from '@shared/services/document-position-sync.service';

@Component({
    selector: 'rev-doc-rule-tile',
    templateUrl: './rev-doc-rule-tile.component.html',
    styleUrls: ['./rev-doc-rule-tile.component.scss'],
    standalone: false
})
export class RevDocRuleTileComponent implements OnInit, OnDestroy {
  @Input() rule: RevRule;
  @Input() displayPageNum: boolean;
  @Input() isFilterActive: boolean = false;
  @Input() hasNext: boolean = true;
  @Input() hasPrev: boolean = true;
  @Input() propagateActionFromParent = null;
  @Input() referenceDocuments: ReferenceDocument[] = [];
  @Output() onToggleRuleView = new EventEmitter();
  @Output() onGoToRulePage = new EventEmitter();
  @Output() onUpdateSpellingIssues = new EventEmitter();
  @Output() onToggleRuleViewSwitchDisabled = new EventEmitter();
  @Output() showNextRuleEmitter = new EventEmitter();
  @Output() showPrevRuleEmitter = new EventEmitter();
  @Output() onToggleRequestedRule = new EventEmitter();

  claimToolLabel: string = 'translate.claim.tool';

  private destroy$ = new Subject();

  ruleStatusOptions: DataElement[] = [];
  ruleDetailsOpenSw: boolean = false;
  focusRuleDetailsOnOpen: boolean;
  un: Unsubscribe;
  showClaimToolDialog: boolean = false;
  claimToolElement: ClaimToolElement;
  private childrenSelectionCancel$ = new Subject<void>();

  selectedRefDoc: ReferenceDocument | null = null;

  @ContentChild('childTemplate') childTemplate!: TemplateRef<any>;

  @ContentChild('contentTemplate') contentTemplate!: TemplateRef<any>;

  @ContentChild('imageTemplate') imageTemplate!: TemplateRef<any>;
  @ContentChild('footnotesTemplate') footnotesTemplate!: TemplateRef<any>;

  @ContentChild('tablesTemplate') tablesTemplate!: TemplateRef<any>;

  get editable(): boolean {
    return (
      this.ngRedux.getState().curRevDoc.statusCode == RevDocStatusEnum.UNDERWAY && !this.ngRedux.getState().curRevDoc.isProcessRunning
    );
  }

  get curPage(): number {
    return this.ngRedux.getState().curRevDocPageNum;
  }

  get isPhrase(): boolean {
    return !this.rule.isImage;
  }

  get isShowIssuesOnFullMatchEnabled(): boolean {
    return this.sharedService.isShowIssuesOnFullMatchEnabled();
  }

  get imageCategoriesForDoc() {
    return this.ngRedux.getState().imageCategoriesForDoc;
  }

  get libraryMismatch() {
    if (this.rule.revRuleSimilarityEvent == 'POTENTIAL_NEW') {
      return false;
    }
    return this.getFindingEventCategory('TEXT_MISMATCH');
  }

  get referenceIssues() {
    if (!this.isShowIssuesOnFullMatchEnabled && this.rule.revStatusCode == RevStatus.PASSED) {
      return false;
    }
    return this.getFindingEventCategory('REFERENCE_ISSUE');
  }

  get contentPartMismatch() {
    if (!this.isShowIssuesOnFullMatchEnabled && this.rule.revStatusCode == RevStatus.PASSED) {
      return false;
    }
    return this.getFindingEventCategory('MANDATORY_ASSOC_CONTENT_ISSUE');
  }

  get spellingGrammarIssues() {
    if (!this.isShowIssuesOnFullMatchEnabled && this.rule.revStatusCode == RevStatus.PASSED) {
      return false;
    }
    return this.getFindingEventCategory('SPELLING_GRAMMAR_ISSUE');
  }

  get formatMismatch() {
    if (!this.isShowIssuesOnFullMatchEnabled && this.rule.revStatusCode == RevStatus.PASSED) {
      return false;
    }
    return this.getFindingEventCategory('FORMAT_MISMATCH');
  }

  get consistencyIssues() {
    if (!this.isShowIssuesOnFullMatchEnabled && this.rule.revStatusCode == RevStatus.PASSED) {
      return false;
    }
    return this.getFindingEventCategory('CONSISTENCY_ISSUE');
  }

  get fairBalanceIssues() {
    if (!this.isShowIssuesOnFullMatchEnabled && this.rule.revStatusCode == RevStatus.PASSED) {
      return false;
    }
    return this.getFindingEventCategory('FAIR_BALANCE_RISK_ISSUE');
  }

  get complianceIssues() {
    if (!this.isShowIssuesOnFullMatchEnabled && this.rule.revStatusCode == RevStatus.PASSED) {
      return false;
    }
    return this.getFindingEventCategory('COMPLIANCE_ISSUE');
  }

  getFindingEventCategory(findingEventCategory: string) {
    let matchFindingCategoryDescs = this.rule.revRuleFindingCategoryDescs.filter(
      revRuleFindingCategoryDesc => findingEventCategory == revRuleFindingCategoryDesc[0]
    );
    return matchFindingCategoryDescs.length > 0 ? matchFindingCategoryDescs[0] : null;
  }

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private translateService: TranslateService,
    private ngRedux: NgRedux<AppState>,
    private sharedService: SharedService,
    private revService: RevService,
    private commonService: CommonService,
    private propagateActionsFromParentService: PropagateActionsFromParentService,
    private reevalJobService: ReevalJobService,
    private documentPositionSyncService: DocumentPositionSyncService
  ) {
    this.un = this.ngRedux.subscribe(() => {
      const appState = this.ngRedux.getState();
      switch (appState._currentAction) {
        case actionsList.REV_RULE_ANN_CLICKED:
          if (
            (appState._currentActionData as RevRule).revRuleId ==
            this.rule.revRuleId
          ) {
            this.onRuleDetailsClicked(null);
          }
          break;
      }
    });
  }

  scrollIntoView(): void {
    const rulesContainer = this.elementRef.nativeElement.closest('.tbl-container') as HTMLElement | null;
    if (!rulesContainer) {
      return;
    }

    this.documentPositionSyncService.scrollElementToTop(
      rulesContainer,
      this.elementRef.nativeElement
    );
  }

  setScrollTarget(isTarget: boolean): void {
    this.elementRef.nativeElement
      .querySelector('.tile-container')
      ?.classList.toggle('scroll-target', isTarget);
  }

  get reevalTarget(): ReevalJobTarget {
    return {
      kind: 'REV',
      docId: this.ngRedux.getState().curRevDoc?.docId,
      elementId: this.rule.isImage ? this.rule.imageId : this.rule.phraseId,
      isImage: this.rule.isImage
    };
  }

  onReevalRefresh(): void {
    this.reevalJobService.refreshTile(this.reevalTarget).subscribe();
  }

  ngOnInit(): void {
    this.initRuleStatusOptions();
    // the rule is refreshed in place, so ngOnInit does not run again for its new actions
    this.reevalJobService.tileRefreshed$(this.reevalTarget)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.initRuleStatusOptions());

    if (
      this.ngRedux.getState().dlRevDoc &&
      this.ngRedux.getState().dlRevDoc.ruleId &&
      this.ngRedux.getState().dlRevDoc.ruleId == this.rule.revRuleId &&
      !this.ngRedux.getState().dlRevDoc.dlRuleHandled
    ) {
      this.onRuleDetailsClicked(null);
      this.ngRedux.getState().dlRevDoc.dlRuleHandled = true;
    }
    if (this.rule.revRuleContentType =='TEXT_CONTENT') {
      this.claimToolLabel = 'translate.text.tool';
    }
    this.propagateActionFromParent?.pipe(takeUntil(this.destroy$)).subscribe((action: ParentTileChildrenSelectionResponse) => {
      // code executed in child tile
      let selection;
        if (!this.isPhrase) {
          const imageChildrenSelection = action.images.find(image => image.image_id == this.rule.imageId);
          selection = imageChildrenSelection.action;
        } else {
          const phraseChildrenSelection = action.phrases.find(phrase => phrase.phrase_id == this.rule.phraseId);
          selection = phraseChildrenSelection.action;
        }

        if (this.setRuleUserStatus(selection)) {
          
          this.propagateActionsFromParentService.addRevRule(this.rule);
        }
        
    })
  }
  
  private initRuleStatusOptions() {
    let actions = [];
    if (this.rule) {
      actions = this.rule.revRuleActions.map(
        a => (a == 'APPLY_CHANGE') ? 'MARKED_FOR_CHANGES' : a
      ); 
    }
    // show all actions on old docs
    let isNewRuleDoc = !!this.rule.revRuleSimilarityEvent;
    if (isNewRuleDoc) {
      this.ruleStatusOptions =
      this.sharedService.getRevRuleUpdStatusOptions().filter(
        s => {
          return actions.indexOf(s.id) >= 0;
        }
      );
    } else {
      this.ruleStatusOptions = this.sharedService.getRevRuleUpdStatusOptions();
    }

    this.checkIfRevRuleHasSelectedUserStatus(this.rule);
  }

  private setRuleUserStatus(selection: string) {

        if (selection != null) {

          this.rule.isParentActionSelected = true;
          let statusOption = this.ruleStatusOptions.find(ruleStatusOption => ruleStatusOption.id == selection);
          if (statusOption == null) {
            return false;
          }
          this.rule.ruleUserStatus = statusOption;
          this.rule.revStatusCode = statusOption.id;
          this.rule.revStatusDescr = this.ngRedux.getState().revRuleStatusList.find(
            (s) => s.id == this.rule.ruleUserStatus.id
          ).text;
        } else {
          this.rule.revStatusCode = null;
          this.rule.contextCatPhraseId = null;
          this.rule.isParentActionSelected = false;

      }
      return true;
  }

  ngOnDestroy(): void {
    if (this.un) this.un();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onRuleDetailsClicked(event: any) {
    if (event) event.stopPropagation();
    this.focusRuleDetailsOnOpen = false;
    this.openRuleDetails();
  }

  openRuleDetails() {
    this.ruleDetailsOpenSw = true;
    this.rule.viewSw = true;
    this.ngRedux.dispatch({
      type: actionsList.SELECT_REV_RULE,
      data: this.rule,
    });
    if (this.rule.pageNum !== this.curPage) {

      this.revService.pdfAnnotationRenderReady$.pipe(take(1)).subscribe(() => {
        // pdf loaded 
        // render bounding box
        this.ngRedux.getState().curRevDocPageNum = this.rule.pageNum;
        console.log("pdf loaded " + this.rule.revRuleId);
            
          this.showAnnotationForRule();
        

    })
        this.revService.changePdfPage$.next(this.rule.pageNum);
    } else {
      
      this.showAnnotationForRule();
    }

  }

  onRevStatusChange() {
  
    if (this.rule.ruleUserStatus == null) {
      this.rule.revStatusCode = null;
      this.rule.contextCatPhraseId = null;
    } else if (this.rule.revStatusCode == this.rule.ruleUserStatus.id) {
      this.rule.revStatusCode = null;
      this.rule.ruleUserStatus = null;
      this.rule.contextCatPhraseId = null;
    } else {
      
      this.rule.revStatusCode = this.rule.ruleUserStatus.id;
      this.rule.revStatusDescr = this.ngRedux
        .getState()
        .revRuleStatusList.find(
          (s) => s.id == this.rule.ruleUserStatus.id
        ).text;

      if (!this.rule.contextCatPhraseId && this.rule.ruleCatMatches.length > 0) {
        const match = this.rule.ruleCatMatches[0];
        if (match.catPhrase) {
          this.rule.contextCatPhraseId = match.catPhrase.phraseId;
        } else if (match.catImage) {
          this.rule.contextCatPhraseId = match.catImage.imageId;
        }
      }
    }
    if (Config.assetConfigData['showChildrenTiles'] && (Object.keys(this.rule.children).length > 0 || this.rule.contentChildren.length || this.rule.imageChildren.length) ) {

      let actionId = null;
      this.childrenSelectionCancel$.next();
      
      if (this.rule.ruleUserStatus != null) {
        actionId = this.rule.ruleUserStatus.id;
      }
      this.checkIfRevRuleHasSelectedUserStatus(this.rule);
      this.commonService.getChildrenSelection(this.ngRedux.getState().curRevDoc.docId, this.rule.phraseId, actionId, true).pipe(takeUntil(this.childrenSelectionCancel$)).subscribe((selections: ParentTileChildrenSelectionResponse) => {
          
          //CODE EXECUTED IN PARENT TILE

          //this.revService.propagateActionFromParentRule$.next({rule: this.rule, action: selections});
          this.propagateActionsFromParentService.clear();
          this.propagateActionsFromParentService.addRevRule(this.rule);
          this.rule.propagateActionFromParent.next(selections);
          let rulesToUpdate: RevRule[] = this.propagateActionsFromParentService.getRevRulesToUpdate();
          this.revService.updRevRule(this.ngRedux.getState().curRevDoc, rulesToUpdate, 
          this.rule.revStatusCode, this.rule.contextCatPhraseId).pipe(takeUntil(this.childrenSelectionCancel$)).subscribe((data) =>{
          
            rulesToUpdate.forEach((ruleToUpdate) => {
              let updRule = data.find((d) => d.revRuleId == ruleToUpdate.revRuleId);
              ruleToUpdate.revStatusCode = updRule.revStatusCode;
              ruleToUpdate.revStatusDescr = updRule.revStatusDescr;
              ruleToUpdate.ruleUserStatus = updRule.ruleUserStatus;
              //this.onToggleRuleView.next(ruleToUpdate);
              ruleToUpdate.viewSw = true;
              this.onToggleRequestedRule.next(ruleToUpdate);
              this.checkIfRevRuleHasSelectedUserStatus(ruleToUpdate);
              this.sharedService.checkConsistencyCheckResultsPanelClose.next();
              // add new event
            })

            rulesToUpdate.forEach((r) => {
              r.checkedSw = false;
            });

        },
      (error) => {
          this.sharedService.showInfoMessage({
            messageKey: 'translate.error.propagateActionFromParentRule',
            infoType: 'error'
          });
          this.checkIfRevRuleHasSelectedUserStatus(this.rule);
      })                 

    })

    } else {
    this.checkIfRevRuleHasSelectedUserStatus(this.rule);
    this.revService
      .updRevRule(
        this.ngRedux.getState().curRevDoc,
        [this.rule],
        this.rule.revStatusCode,
        this.rule.contextCatPhraseId
      )
      .subscribe(
        (data) => {
          this.rule.revStatusCode = data[0].revStatusCode;
          this.rule.revStatusDescr = data[0].revStatusDescr;
          this.rule.annColor = data[0].annColor;
          //this.onToggleRuleView.emit();
          this.checkIfRevRuleHasSelectedUserStatus(this.rule);
          this.sharedService.checkConsistencyCheckResultsPanelClose.next();
          this.onToggleRuleViewSwitchDisabled.emit();
          
          if (this.rule.revStatusCode == RevStatus.DISMISSED) {
            this.focusRuleDetailsOnOpen = true;
            this.openRuleDetails();
          }
        },
        (err) => {
          this.checkIfRevRuleHasSelectedUserStatus(this.rule);
        }
      );
    }
  }

  toggleRuleView(event: any) {
    this.rule.viewSw = !this.rule.viewSw;
    this.onToggleRuleView.emit();
    if (this.rule.viewSw) {
      this.revService.focusAnnotationForRule$.next(this.rule);
    }
  }

  goToPage() {
    this.onGoToRulePage.emit();
  }

  notifySpellingIssuesUpdate() {
    this.onUpdateSpellingIssues.emit();
  }

  showClaimTool() {
    // create new component
    this.ruleDetailsOpenSw = false;
    this.showClaimToolDialog = true;
    const claimToolSetup = this.revService.createClaimToolSetup(this.rule, this.ngRedux.getState().curRevDoc);
    this.claimToolElement = claimToolSetup.element;
    this.ngRedux.dispatch({
      type: actionsList.GET_CLAIM_TOOL_ELEMENT,
      data: this.claimToolElement
    })
      this.ngRedux.dispatch({
      type: actionsList.GET_CLAIM_TOOL_SAVE_CHANGES_INITIAL_VALUES,
      data: claimToolSetup.saveChangesInitialValues
    });
    this.ngRedux.dispatch({
      type: actionsList.SELECT_REV_RULE,
      data: this.rule,
    }); 
    
  }

  showNextRule() {
    this.ruleDetailsOpenSw = false;
    this.showNextRuleEmitter.emit();
  }

  showPrevRule() {
    this.ruleDetailsOpenSw = false;
    this.showPrevRuleEmitter.emit();
  } 
  showDetailsDialog() {
    this.showClaimToolDialog = false;
    this.openRuleDetails();
  }
  showAnnotationForRule() {
   
     //this.onShowRuleView.emit();
     this.revService.showAnnotationForRule$.next(this.rule); 
  }

  hideRuleAnnotation() {
    this.revService.hideAnnotationForRule$.next(this.rule);
  }

  closeDetailsDialog() {
    
    this.ruleDetailsOpenSw = false;
/*     this.rule.viewSw = false;
    this.destroy$.next();
    this.destroy$.complete();
    this.destroy$ = new Subject(); */
  }

  canViewRefDoc(ref: ReferencePhrase): boolean {
    for (let refDoc of this.referenceDocuments) {
      if (refDoc.mappedReferenceTexts.includes(ref.refCatMatchTxt)) {
        return true;
      }

      for (let consistencyCheckResult of ref.anchors) {
        if (consistencyCheckResult.refDocId == refDoc.docId) {
          return true;
        }
      }
    }
    return false;
  }

  viewRefDoc(ref: ReferencePhrase) {
    for (let refDoc of this.referenceDocuments) {
      if (refDoc.mappedReferenceTexts.includes(ref.refCatMatchTxt)) {
        this.selectedRefDoc = refDoc;
        return;
      }

      for (let consistencyCheckResult of ref.anchors) {
        if (consistencyCheckResult.refDocId == refDoc.docId) {
          this.selectedRefDoc = refDoc;
          return;
        }
      }
    }
  }
  private  checkIfRevRuleHasSelectedUserStatus(rule: RevRule) {
          let foundIndex = this.ruleStatusOptions?.findIndex((statusOption) => statusOption.id == rule.ruleUserStatus?.id);
      if (foundIndex != -1) {

        rule.disableConsistencyCheckResultsActions = true;
      } else {
        
        rule.disableConsistencyCheckResultsActions = false;
      }
  }
}
